import * as React from 'react'
import i18next from 'i18next'
import { inject } from 'mobx-react'
import { API, ApiError, RemoteResource, VideoStream, getResourceUrl } from '../../services/api'
import { downloadFile } from '../../utils/file'
import { TagsInput } from '../TagsInput'
import { MODAL_STORE, ModalStore, ModalData } from '../../stores/ModalStore'
import { useEffect, useRef, useState, useCallback } from 'react'
import { disableScroll, enableScroll } from '../../utils/scroll'
import { Header, DownloadSize, DOWNLOAD_SIZE_PIXELS } from './components/header/Header'
import { Tags } from './components/tags/Tags'
import { Spinner } from '../Spinner'
import { Skeleton } from '../Skeleton'
import { USER_STORE, WORKSPACES_STORE } from '../../stores'
import { UserStore } from '../../stores/UserModel'
import { WorkspacesStore } from '../../stores/WorkspaceStore'
import { RouteComponentProps } from 'react-router'
import { IPopulatedUserPhoto, IUserPhoto } from '../../models/user_model'
import { ConfirmModal } from '../ConfirmModal'
import Hls from 'hls.js'
import { NavigationArrow } from '../NavigationArrow/NavigationArrow'

// SVG Icons
const DescriptionIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-gray-400 shrink-0">
    <path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const DeviceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-gray-400 shrink-0">
    <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-gray-400 shrink-0">
    <path d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const LocationIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-gray-400 shrink-0">
    <path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const SizeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-gray-400 shrink-0">
    <path d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-gray-400 shrink-0">
    <path d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const PhotoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5 text-gray-400 shrink-0">
    <path d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-4 w-4">
    <path d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)



interface PhotoDisplayerProps extends Partial<RouteComponentProps<{ photoId: string }>> {
  [MODAL_STORE]?: ModalStore
  [WORKSPACES_STORE]?: WorkspacesStore
  [USER_STORE]?: UserStore
  onClose: () => void
  onDelete: (photoId: string) => void
  onTagClick?: (tag: string) => void
  onNavigate?: (photoId: string, index: number) => void
  onNearEnd?: () => void
  onNearStart?: () => void
  /** flat index of photos[0] when photos is a window into a larger list */
  countOffset?: number
  /** full library size for the "n / total" counter; defaults to photos.length */
  totalCount?: number
  photos: Array<RemoteResource>
  photoId: string
}

interface PhotoDisplayerState {
  isInterfaceVisible: boolean
  photo?: IUserPhoto | IPopulatedUserPhoto
  error?: ApiError
  imageLoaded: boolean
  smallLoaded: boolean
  updateMode: boolean
  isComponentMounted: boolean
  index: number
}

type UpdatePhotoData = {
  tags: Array<string>
  description: string
  isPublic: boolean
}

const PhotoDisplayer: React.FC<PhotoDisplayerProps> = (props) => {
  const backgroundElem = useRef<HTMLDivElement>(null)
  const containerElem = useRef<HTMLDivElement>(null)

  const photoId = props.photoId ?? props.match?.params.photoId

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showCounter, setShowCounter] = useState(true)
  const [fullscreenMode, setFullscreenMode] = useState(false)
  const [fullscreenUIVisible, setFullscreenUIVisible] = useState(false)
  const [fullscreenCounterVisible, setFullscreenCounterVisible] = useState(true)
  const fullscreenMouseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Video state
  const [videoStreams, setVideoStreams] = useState<VideoStream[] | null>(null)
  const [videoLoading, setVideoLoading] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const fullscreenVideoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const fullscreenHlsRef = useRef<Hls | null>(null)

  const [state, _setState] = useState<PhotoDisplayerState>({
    isInterfaceVisible: true,
    imageLoaded: false,
    smallLoaded: false,
    updateMode: false,
    photo: undefined,
    index: props.photos.findIndex((photo) => photo.id === photoId),
    isComponentMounted: false,
  })
  const setState = (newState: Partial<PhotoDisplayerState>): void =>
    _setState((oldState) => ({ ...oldState, ...newState }))

  const [updatedPhoto, _setUpdatedPhoto] = useState<UpdatePhotoData>({
    tags: [],
    description: '',
    isPublic: undefined,
  })
  const setUpdatedPhoto = (newState: Partial<UpdatePhotoData>): void =>
    _setUpdatedPhoto((oldState) => ({ ...oldState, ...newState }))

  const closePhoto = async (event?: React.MouseEvent) => {
    if (state.updateMode) {
      // TODO: Add confirmation when in update mode
    }
    props.onClose()
  }

  // Navigation
  const currentIndex = props.photos.findIndex((p) => p.id === photoId)
  const currentResource = props.photos[currentIndex] // Available immediately
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < props.photos.length - 1
  const PREFETCH_THRESHOLD = 10

  const navigateTo = (index: number) => {
    if (index < 0 || index >= props.photos.length) return
    const targetPhoto = props.photos[index]
    if (!targetPhoto) return
    
    // Reset loaded flags – keep old photo visible as placeholder until new one loads
    setState({ imageLoaded: false, smallLoaded: false })
    
    // Check if we need to prefetch more photos
    if (props.photos.length - index <= PREFETCH_THRESHOLD) {
      props.onNearEnd?.()
    }
    if (index <= PREFETCH_THRESHOLD) {
      props.onNearStart?.()
    }

    props.onNavigate?.(targetPhoto.id, index)
  }

  const goToPrev = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (hasPrev) navigateTo(currentIndex - 1)
  }

  const goToNext = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (hasNext) navigateTo(currentIndex + 1)
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (state.updateMode) return // Don't navigate when editing
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goToPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goToNext()
      } else if (e.key === 'Escape') {
        if (fullscreenMode) {
          setFullscreenMode(false)
        } else {
          closePhoto()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [currentIndex, state.updateMode, props.photos.length, fullscreenMode])

  // Auto-hide counter after 2 seconds
  useEffect(() => {
    setShowCounter(true)
    const timer = setTimeout(() => setShowCounter(false), 2000)
    return () => clearTimeout(timer)
  }, [currentIndex])

  // Reset fullscreen counter visibility when entering fullscreen or navigating
  useEffect(() => {
    if (fullscreenMode) {
      setFullscreenCounterVisible(true)
      const timer = setTimeout(() => setFullscreenCounterVisible(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [fullscreenMode, currentIndex])

  // Reset UI visibility when entering fullscreen (start hidden)
  useEffect(() => {
    if (fullscreenMode) {
      setFullscreenUIVisible(false)
    }
    return () => {
      if (fullscreenMouseTimer.current) clearTimeout(fullscreenMouseTimer.current)
    }
  }, [fullscreenMode])

  const handleFullscreenMouseMove = () => {
    setFullscreenUIVisible(true)
    if (fullscreenMouseTimer.current) clearTimeout(fullscreenMouseTimer.current)
    fullscreenMouseTimer.current = setTimeout(() => setFullscreenUIVisible(false), 2000)
  }

  // Check if current resource is a video
  const isVideo = currentResource?.type === 'video'

  // Fetch video streams when opening a video
  useEffect(() => {
    if (!isVideo || !currentResource) {
      setVideoStreams(null)
      return
    }

    const fetchVideoStreams = async () => {
      setVideoLoading(true)
      try {
        const response = await API.getInstance().getVideoStreams({
          workspaceId: props[WORKSPACES_STORE].activeWorkspaceId,
          resourceId: currentResource.id,
        })
        if (response.ok && response.data) {
          setVideoStreams(response.data)
        }
      } catch (err) {
        console.error('Error fetching video streams', err)
      } finally {
        setVideoLoading(false)
      }
    }

    fetchVideoStreams()
  }, [currentResource?.id, isVideo])

  // Setup HLS player for main video
  const setupHls = useCallback((video: HTMLVideoElement, streamUrl: string, signature: string, hlsRefToUse: React.MutableRefObject<Hls | null>) => {
    // Cleanup previous instance
    if (hlsRefToUse.current) {
      hlsRefToUse.current.destroy()
      hlsRefToUse.current = null
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        xhrSetup: (xhr) => {
          xhr.setRequestHeader('signature', signature)
        }
      })
      hls.loadSource(streamUrl)
      hls.attachMedia(video)
      hlsRefToUse.current = hls
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari) - can't set custom headers
      video.src = streamUrl
    }
  }, [])

  // Setup main video player when streams are loaded
  useEffect(() => {
    if (!videoStreams || !videoRef.current) return
    
    const hlsStream = videoStreams.find(s => s.type === 'hls')
    if (hlsStream) {
      const streamUrl = hlsStream.url
      setupHls(videoRef.current, streamUrl, hlsStream.signature, hlsRef)
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [videoStreams, setupHls])

  // Setup fullscreen video player when entering fullscreen with video
  useEffect(() => {
    if (!fullscreenMode || !isVideo || !videoStreams || !fullscreenVideoRef.current) return
    
    const hlsStream = videoStreams.find(s => s.type === 'hls')
    if (hlsStream) {
      const streamUrl = hlsStream.url
      setupHls(fullscreenVideoRef.current, streamUrl, hlsStream.signature, fullscreenHlsRef)
    }

    return () => {
      if (fullscreenHlsRef.current) {
        fullscreenHlsRef.current.destroy()
        fullscreenHlsRef.current = null
      }
    }
  }, [fullscreenMode, isVideo, videoStreams, setupHls])

  const handleDeletePhoto = async () => {
    try {
      await API.getInstance().deleteUserPhoto({
        id: photoId,
        workspaceId: props[WORKSPACES_STORE].activeWorkspaceId,
      })
    } catch (err) {
      console.log('Error during photo deletion', err)
    } finally {
      setShowDeleteModal(false)
      props.onDelete(photoId)
    }
  }

  const fetchPhoto = async (): Promise<void> => {
    const loadPhotoImage = async (url: string) =>
      new Promise<void>((res) => {
        const img = new Image()
        img.onload = () => {
          setState({ imageLoaded: true })
          res()
        }
        img.src = url
      })

    const loadSmallImage = (url: string) =>
      new Promise<void>((res) => {
        const img = new Image()
        img.onload = () => {
          setState({ smallLoaded: true })
          res()
        }
        img.src = url
      })

    const fetchPhotoDataObject = async (photoId: string): Promise<IPopulatedUserPhoto> => {
      const photoResponse = await API.getInstance().getPhoto(
        props[WORKSPACES_STORE].activeWorkspaceId,
        photoId
      )
      if (photoResponse.ok) {
        const photo = photoResponse.data
        setState({ photo })
        return photo
      } else {
        throw new Error('Cannot fetch photo.')
      }
    }

    try {
      const photo = await fetchPhotoDataObject(photoId)
      setState({ imageLoaded: false, smallLoaded: false })
      if (photo.small_url) {
        loadSmallImage(photo.small_url)
      }
      loadPhotoImage(photo.large_url)
    } catch (err) {
      closePhoto()
    }
  }

  useEffect(() => {
    backgroundElem.current?.focus()
    fetchPhoto()
    disableScroll()
    return () => {
      enableScroll()
    }
  }, [photoId])



  const searchPhotosByTag = (tag: string) => {
    props.onTagClick?.(tag)
  }

  const preventPropagation = (event: React.MouseEvent) => {
    event.stopPropagation()
  }

  const handleDownload = (size: DownloadSize) => {
    const workspaceId = props[WORKSPACES_STORE]?.activeWorkspace?.id
    const apiInstance = API.getInstance()
    
    if (photo && workspaceId) {
      if (size === 'original') {
        // Download original file directly via link (cookie auth)
        downloadFile(photo.original_url_download, photo.original_file_name)
      } else {
        // Use resized endpoint for smaller sizes
        const pixels = DOWNLOAD_SIZE_PIXELS[size]
        const isLandscape = photo.width >= photo.height
        const url = apiInstance.getResizedPhotoUrl({
          workspaceId,
          resourceId: photo.id,
          width: isLandscape ? pixels : undefined,
          height: isLandscape ? undefined : pixels,
        })
        const filename = `${photo.original_file_name.replace(/\.[^/.]+$/, '')}_${size}.webp`
        downloadFile(url, filename)
      }
    } else if (currentResource) {
      downloadFile(getResourceUrl(currentResource, 'original'), currentResource.name)
    }
  }

  const { photo } = state
  const isOwner = photo?.owner?.id === props[USER_STORE]?.id

  const displayPhoto = photo?.id === photoId ? photo : undefined
  const displaySmallUrl = displayPhoto?.small_url || (currentResource && getResourceUrl(currentResource, 'small'))
  const displayLargeUrl = displayPhoto?.large_url || (currentResource && getResourceUrl(currentResource, 'large'))
  const displayWidth = displayPhoto?.width || currentResource?.width
  const displayHeight = displayPhoto?.height || currentResource?.height

  // Calculate image dimensions; the initial guess is clamped to the viewport so the
  // first paint (before measurement) can't overflow horizontally on small screens
  const [mediaBox, setMediaBox] = useState<{ width: number; height: number }>(() => {
    const width = Math.min(600, (typeof window === 'undefined' ? 600 : window.innerWidth) - 48)
    return { width, height: Math.round((width * 2) / 3) }
  })

  useEffect(() => {
    const calculateMediaBox = () => {
      if (!displayWidth || !displayHeight) return
      const maxHeight = window.innerHeight * 0.7
      const maxWidth = (containerElem.current?.clientWidth ?? window.innerWidth) - 48
      const ratio = displayWidth / displayHeight
      const width = Math.min(maxWidth, maxHeight * ratio)
      const height = width / ratio
      setMediaBox({ width, height })
    }

    calculateMediaBox()
    window.addEventListener('resize', calculateMediaBox)
    return () => window.removeEventListener('resize', calculateMediaBox)
  }, [displayWidth, displayHeight])

  const InfoRow = ({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) => (
    <div className="flex items-start gap-3 py-2">
      {icon}
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <span className="text-sm text-gray-500">{subtitle}</span>
      </div>
    </div>
  )

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backgroundElem}
        tabIndex={1}
        onClick={closePhoto}
        className="fixed inset-0 z-50 bg-black/80 overflow-y-auto overflow-x-hidden cursor-zoom-out outline-none"
      >
        {/* Navigation arrows - desktop only */}
        {hasPrev && (
          <NavigationArrow
            direction="left"
            onClick={goToPrev}
            className="hidden md:flex fixed left-4 top-1/2 -translate-y-1/2 z-50"
          />
        )}
        {hasNext && (
          <NavigationArrow
            direction="right"
            onClick={goToNext}
            className="hidden md:flex fixed right-4 top-1/2 -translate-y-1/2 z-50"
          />
        )}

        {/* Photo counter */}
        <div 
          className={`hidden md:block fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-3 py-1.5 rounded-full bg-black/50 text-white text-sm backdrop-blur-sm transition-opacity duration-300 ${showCounter ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          {(props.countOffset ?? 0) + currentIndex + 1} / {props.totalCount ?? props.photos.length}
        </div>

        {/* Fullscreen mode overlay */}
        {fullscreenMode && (
          <div 
            className={`fixed inset-0 z-[60] bg-black flex items-center justify-center ${fullscreenUIVisible ? 'cursor-zoom-out' : 'cursor-none'}`}
            onClick={(e) => { e.stopPropagation(); setFullscreenMode(false); }}
            onMouseMove={handleFullscreenMouseMove}
          >
            {/* Navigation arrows in fullscreen */}
            {hasPrev && (
              <NavigationArrow
                direction="left"
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                className={`fixed left-4 top-1/2 -translate-y-1/2 z-[70] transition-all ${fullscreenUIVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              />
            )}
            {hasNext && (
              <NavigationArrow
                direction="right"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className={`fixed right-4 top-1/2 -translate-y-1/2 z-[70] transition-all ${fullscreenUIVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              />
            )}

            {/* Fullscreen image/video */}
            {isVideo && videoStreams ? (
              <video
                ref={fullscreenVideoRef}
                controls
                autoPlay
                className="max-w-full max-h-full object-contain"
                poster={currentResource ? getResourceUrl(currentResource, 'small') : undefined}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                alt={photo?.description || currentResource?.name || 'Photo'}
                src={photo?.large_url || (currentResource && getResourceUrl(currentResource, 'large'))}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); setFullscreenMode(false); }}
              className={`fixed top-4 left-4 z-[70] flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all backdrop-blur-sm ${fullscreenUIVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              aria-label="Zamknij"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Photo counter in fullscreen */}
            <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] px-3 py-1.5 rounded-full bg-black/50 text-white text-sm backdrop-blur-sm transition-opacity duration-300 ${fullscreenCounterVisible ? 'opacity-100' : 'opacity-0'}`}>
              {(props.countOffset ?? 0) + currentIndex + 1} / {props.totalCount ?? props.photos.length}
            </div>
          </div>
        )}

        {/* Modal container */}
        <div className="flex min-h-full items-start justify-center p-0 md:p-5 md:px-20">
          {/* Modal panel */}
          <div
            ref={containerElem}
            onClick={preventPropagation}
            className="relative w-full md:w-4/5 max-w-5xl min-h-screen md:min-h-0 bg-white md:rounded-lg shadow-xl cursor-default md:my-5"
          >
            <Header photo={photo} canDownload={!!(photo || currentResource)} isVideo={isVideo} onClose={closePhoto} onDownload={handleDownload} />

            <div className="px-4 pb-6 md:px-6">
              {/* Image/Video */}
              <div className="flex justify-center mb-6">
                <div
                  className={`relative overflow-hidden ${!isVideo ? 'cursor-zoom-in' : ''}`}
                  style={{ backgroundColor: photo?.accent_color || '#f3f4f6' }}
                  onClick={() => !isVideo && setFullscreenMode(true)}
                >
                  {isVideo ? (
                    // Video player
                    videoLoading ? (
                      <div
                        className="flex items-center justify-center bg-gray-900"
                        style={{ width: mediaBox.width, height: mediaBox.height }}
                      >
                        <div className="flex flex-col items-center gap-3">
                          <Spinner size="md" tone="onDark" />
                          <span className="text-white/70 text-sm">{i18next.t('loading.video')}</span>
                        </div>
                      </div>
                    ) : videoStreams ? (
                      <div className="mx-auto" style={{ width: mediaBox.width, height: mediaBox.height }}>
                        <video
                          ref={videoRef}
                          controls
                          className="h-full w-full object-contain shadow-lg"
                          poster={currentResource ? getResourceUrl(currentResource, 'small') : undefined}
                        />
                      </div>
                    ) : (
                      <div
                        className="flex items-center justify-center bg-gray-900"
                        style={{ width: mediaBox.width, height: mediaBox.height }}
                      >
                        <span className="text-white/70 text-sm">Nie można załadować wideo</span>
                      </div>
                    )
                  ) : (photo || currentResource) ? (
                    <div className="relative mx-auto" style={{ width: mediaBox.width, height: mediaBox.height }}>
                      {/* Background placeholder while images load */}
                      <div className="absolute inset-0 bg-gray-200" />
                      {/* Show small thumbnail immediately (cached from gallery grid) */}
                      {displaySmallUrl && (
                        <img
                          key={`small-${photoId}`}
                          alt={displayPhoto?.description || currentResource?.name || 'Photo'}
                          src={displaySmallUrl}
                          className="absolute inset-0 h-full w-full object-contain shadow-lg"
                        />
                      )}
                      {/* Overlay the large image once loaded */}
                      {state.imageLoaded && displayLargeUrl && (
                        <img
                          key={`large-${photoId}`}
                          alt={displayPhoto?.description || currentResource?.name || 'Photo'}
                          src={displayLargeUrl}
                          className="absolute inset-0 h-full w-full object-contain shadow-lg"
                        />
                      )}
                    </div>
                  ) : (
                    <Skeleton className="aspect-[3/2] w-full max-w-[600px]" />
                  )}
                </div>
              </div>

              {/* Tags section */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Tagi</h3>
                {photo && state.updateMode ? (
                  <TagsInput
                    workspaceId={props[WORKSPACES_STORE].activeWorkspaceId}
                    initialTags={photo.tags}
                    onChange={(tags) => setUpdatedPhoto({ tags })}
                  />
                ) : (
                  <Tags tags={photo?.tags} onTagClick={searchPhotosByTag} />
                )}
              </div>

              {/* Description */}
              {photo && state.updateMode ? (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Opis</h3>
                  <textarea
                    defaultValue={updatedPhoto.description}
                    onChange={(e) => setUpdatedPhoto({ description: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                    rows={3}
                  />
                </div>
              ) : (
                photo?.description && (
                  <InfoRow icon={<DescriptionIcon />} title="Opis" subtitle={photo.description} />
                )
              )}

              {/* Photo metadata */}
              <div className="space-y-1 border-t border-gray-100 pt-4 mt-4">
                {photo?.device && (
                  <InfoRow
                    icon={<DeviceIcon />}
                    title={photo.device.model}
                    subtitle={photo.device.make}
                  />
                )}
                {photo?.location && (
                  <InfoRow
                    icon={<LocationIcon />}
                    title={photo.location}
                    subtitle={`${photo.lat}, ${photo.lng}`}
                  />
                )}
                {photo && (
                  <InfoRow
                    icon={<SizeIcon />}
                    title="Rozmiar"
                    subtitle={`${(photo.size / 1_000_000).toPrecision(2)}MB`}
                  />
                )}
                {photo?.date_taken && (
                  <InfoRow
                    icon={<CameraIcon />}
                    title="Zrobiono"
                    subtitle={new Date(photo.date_taken).toLocaleString()}
                  />
                )}
                {photo && (
                  <InfoRow
                    icon={<UploadIcon />}
                    title="Przesłano"
                    subtitle={new Date(photo.created_at).toLocaleString()}
                  />
                )}
                {photo && (
                  <InfoRow
                    icon={<PhotoIcon />}
                    title={photo.original_file_name}
                    subtitle={`${photo.width}x${photo.height}`}
                  />
                )}
              </div>

              {/* Action buttons */}
              {photo && isOwner && (
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
                  {state.updateMode ? (
                    <>
                      <button
                        onClick={() => setState({ updateMode: false })}
                        className="inline-flex items-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        Anuluj
                      </button>
                      <button
                        onClick={() => {
                          setState({ updateMode: false })
                          API.getInstance()
                            .updatePhoto(photoId, {
                              tags: updatedPhoto.tags,
                              description: updatedPhoto.description,
                              isPublic: updatedPhoto.isPublic,
                              workspaceId: props[WORKSPACES_STORE].activeWorkspaceId,
                            })
                            .then((response) => {
                              if (response.ok) {
                                setState({ photo: response.data })
                              }
                            })
                        }}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 transition-colors"
                      >
                        Zapisz
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
                      >
                        <DeleteIcon />
                        Usuń
                      </button>
                      <button
                        onClick={() => {
                          setUpdatedPhoto({
                            description: photo.description,
                            tags: photo.tags,
                          })
                          setState({ updateMode: true })
                        }}
                        className="inline-flex items-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        Edytuj
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeletePhoto}
        title="Usuń zdjęcie"
        description="Czy na pewno chcesz usunąć to zdjęcie? Zdjęcie zostanie usunięte z każdego albumu w którym się znajduje. Tej operacji nie da się cofnąć."
        confirmLabel="Usuń zdjęcie"
        cancelLabel="Anuluj"
        variant="danger"
      />
    </>
  )
}

export default inject(WORKSPACES_STORE)(inject(MODAL_STORE)(inject(USER_STORE)(PhotoDisplayer)))
